import { Info } from "lucide-react";
import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useImmer } from "use-immer";

import {
  IgButton,
  IgCheckbox,
  IgPaper,
  IgTooltip,
  IgTypography,
} from "@intelligems/ig-theme";
import {
  FormControlLabel,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  useTheme,
} from "@mui/material";
import { renderConditionText } from "@src/features/experiments/utils/validators";
import {
  selectedAudienceRolloutInfo,
  selectedAudienceState,
  setSourcesToBeUsed,
} from "@src/store/slices/audiences";
import { RolloutAudienceSources } from "@src/store/slices/audiences/types";

export const RolloutAudienceIndicator = () => {
  const dispatch = useDispatch();
  const rolloutsInfo = useSelector(selectedAudienceRolloutInfo);
  const audienceState = useSelector(selectedAudienceState);
  const [showGenerateSelection, setShowGenerateSelection] = useImmer(false);
  const theme = useTheme();
  const [tempSourcesToUse, setTempSourcesToUse] =
    useImmer<RolloutAudienceSources>({
      testTargeting: Boolean(
        audienceState?.storedAudience &&
          audienceState.storedAudience.filters.length
      ),
      dashboardTargeting: Boolean(
        rolloutsInfo && rolloutsInfo.dashboardTargeting.length
      ),
      analyticTableTargeting: Boolean(
        rolloutsInfo && rolloutsInfo.analyticTableTargeting.length
      ),
    });

  const summary = useMemo(() => {
    if (
      audienceState.storedAudience?.audienceType === "advanced" ||
      audienceState?.audience?.filters.length === 0
    ) {
      return (
        <IgTypography>
          Define an audience below or{" "}
          <IgTypography
            igStyle={"linkNoBold"}
            onClick={() => {
              setShowGenerateSelection(true);
            }}
          >
            generate one based on your test.
          </IgTypography>
        </IgTypography>
      );
    }

    const sourcesToUse: Array<keyof RolloutAudienceSources> = [];
    if (
      audienceState?.storedAudience &&
      audienceState.storedAudience.filters.length &&
      rolloutsInfo?.sourcesUsed.testTargeting
    ) {
      sourcesToUse.push("testTargeting");
    }
    if (
      rolloutsInfo &&
      rolloutsInfo.analyticTableTargeting.length &&
      rolloutsInfo.sourcesUsed.analyticTableTargeting
    ) {
      sourcesToUse.push("analyticTableTargeting");
    }
    if (
      rolloutsInfo &&
      rolloutsInfo.dashboardTargeting.length &&
      rolloutsInfo.sourcesUsed.dashboardTargeting
    ) {
      sourcesToUse.push("dashboardTargeting");
    }

    if (rolloutsInfo) {
      return (
        <IgTypography>
          We've built an audience based on your{" "}
          {sourcesToUse.map((source, index) => (
            <IgTypography key={`source-${index}`}>
              <IgTooltip
                title={renderConditionText(
                  source === "testTargeting" && audienceState.audience
                    ? audienceState.audience.filters[0].expression || []
                    : source === "analyticTableTargeting" &&
                        rolloutsInfo.analyticTableTargeting &&
                        rolloutsInfo.analyticTableTargeting.length
                      ? rolloutsInfo.analyticTableTargeting || []
                      : rolloutsInfo.dashboardTargeting &&
                          rolloutsInfo.dashboardTargeting.length
                        ? rolloutsInfo.dashboardTargeting
                        : []
                )}
              >
                <IgTypography sx={{ textDecoration: "underline dotted" }}>
                  {source === "testTargeting"
                    ? "test targeting"
                    : source === "dashboardTargeting"
                      ? "analytics filters"
                      : `selection in the audiences table`}
                </IgTypography>
              </IgTooltip>
              {sourcesToUse.length <= 1
                ? ""
                : sourcesToUse.length > 2
                  ? ", and "
                  : sourcesToUse.length - 1 > index
                    ? " and "
                    : ""}
            </IgTypography>
          ))}
          {". "}
          You can modify it or{" "}
          <IgTypography
            onClick={() => {
              setShowGenerateSelection(true);
            }}
            igStyle={"link"}
          >
            generate another based on your test.
          </IgTypography>
        </IgTypography>
      );
    }
    return null;
  }, [audienceState, rolloutsInfo]);

  const buildAudienceButtonState = useMemo(() => {
    const sourcesToUse: Array<keyof RolloutAudienceSources> = [];
    if (
      audienceState?.storedAudience &&
      audienceState.storedAudience.filters.length &&
      audienceState.storedAudience.audienceType !== "advanced"
    ) {
      sourcesToUse.push("testTargeting");
    }
    if (rolloutsInfo && rolloutsInfo.analyticTableTargeting.length) {
      sourcesToUse.push("analyticTableTargeting");
    }
    if (rolloutsInfo && rolloutsInfo.dashboardTargeting.length) {
      sourcesToUse.push("dashboardTargeting");
    }

    if (rolloutsInfo) {
      return (
        <IgTypography>
          Click to reuse the
          {sourcesToUse.length > 1
            ? " the relevant audiences from your test."
            : sourcesToUse.map((source) => {
                if (source === "testTargeting") {
                  return " relevant audience from your test.";
                } else if (source === "dashboardTargeting") {
                  return " filters that were active on the analytics page at the time of the rollout.";
                } else if (source === "analyticTableTargeting") {
                  return " specific audience you chose to roll out from the Audiences table on the analytics page.";
                }
              })}
          {sourcesToUse.length > 1 ? (
            <List
              component="div"
              disablePadding
              dense={true}
              sx={{ listStyleType: "disc" }}
            >
              {sourcesToUse.map((source) => (
                <ListItem key={source}>
                  <ListItemText
                    sx={{ display: "list-item", padding: "0px !important" }}
                    key={source}
                  >
                    {source === "testTargeting"
                      ? "The audience of your test."
                      : source === "dashboardTargeting"
                        ? "Filters that were active on the analytics page at the time of rollout."
                        : "The specific audience you chose to roll out from the Audiences table on the analytics page."}
                  </ListItemText>
                </ListItem>
              ))}
            </List>
          ) : null}
          {sourcesToUse.length > 1 && (
            <IgTypography>
              The more options you select, the more narrowly targeted your
              rollout will become.
            </IgTypography>
          )}
          <br />
          You'll be able to review and modify before continuing.
        </IgTypography>
      );
    }
    return null;
  }, [audienceState, rolloutsInfo]);

  const onBuildAudienceClick = () => {
    dispatch(setSourcesToBeUsed(tempSourcesToUse));
    setShowGenerateSelection(false);
  };

  return (
    <Grid item>
      <IgPaper
        sx={{
          backgroundColor: theme.igBackgroundColors.accent,
          padding: "10px 37px",
          // width: "100%",
        }}
      >
        {!showGenerateSelection ? (
          <Grid item container justifyContent={"center"}>
            <IgTypography>{summary}</IgTypography>
          </Grid>
        ) : (
          <Grid
            item
            container
            direction={"row"}
            alignItems={"center"}
            justifyContent={"center"}
            columnSpacing={1}
          >
            {audienceState.storedAudience &&
            audienceState.storedAudience.filters.length ? (
              <Grid item>
                <FormControlLabel
                  control={
                    <IgCheckbox
                      size={"small"}
                      disabled={
                        audienceState.storedAudience?.audienceType ===
                        "advanced"
                      }
                      checked={
                        audienceState.storedAudience?.audienceType ===
                        "advanced"
                          ? false
                          : tempSourcesToUse.testTargeting
                      }
                      onChange={(event) => {
                        setTempSourcesToUse((draft) => {
                          draft.testTargeting = event.target.checked;
                          return draft;
                        });
                      }}
                    />
                  }
                  label={
                    <IgTooltip
                      title={renderConditionText(
                        audienceState?.audience?.filters[0]?.expression ||
                          audienceState?.storedAudience?.filters[0]
                            .expression ||
                          []
                      )}
                    >
                      <IgTypography
                        sx={{
                          textDecoration: "underline dotted",
                        }}
                      >
                        My Test Targeting{" "}
                        {audienceState.storedAudience?.audienceType ===
                          "advanced" && (
                          <IgTooltip
                            title={
                              "Your test uses an Advanced Audience\n" +
                              "for targeting.  We are unable to import\n" +
                              "this into your roll out but you should be\n" +
                              "able to define your own targeting\n" +
                              "conditions below to capture an equivalent\n" +
                              "audience."
                            }
                          >
                            <IconButton color={"error"} size={"small"}>
                              <Info size={18} />
                            </IconButton>
                          </IgTooltip>
                        )}
                      </IgTypography>
                    </IgTooltip>
                  }
                />
              </Grid>
            ) : null}
            {rolloutsInfo && rolloutsInfo.dashboardTargeting.length ? (
              <Grid item>
                <FormControlLabel
                  control={
                    <IgCheckbox
                      size={"small"}
                      checked={tempSourcesToUse.dashboardTargeting}
                      onChange={(event) => {
                        setTempSourcesToUse((draft) => {
                          draft.dashboardTargeting = event.target.checked;
                          return draft;
                        });
                      }}
                    />
                  }
                  label={
                    <IgTooltip
                      title={
                        rolloutsInfo.dashboardTargeting.length
                          ? renderConditionText(
                              rolloutsInfo.dashboardTargeting || []
                            )
                          : ""
                      }
                    >
                      <IgTypography
                        sx={{
                          textDecoration: "underline dotted",
                        }}
                      >
                        My Analytics Filters
                      </IgTypography>
                    </IgTooltip>
                  }
                />
              </Grid>
            ) : null}
            {rolloutsInfo && rolloutsInfo.analyticTableTargeting.length ? (
              <Grid item>
                <FormControlLabel
                  control={
                    <IgCheckbox
                      size={"small"}
                      checked={tempSourcesToUse.analyticTableTargeting}
                      onChange={(event) => {
                        setTempSourcesToUse((draft) => {
                          draft.analyticTableTargeting = event.target.checked;
                          return draft;
                        });
                      }}
                    />
                  }
                  label={
                    <IgTooltip
                      title={renderConditionText(
                        rolloutsInfo.analyticTableTargeting || []
                      )}
                    >
                      <IgTypography
                        sx={{
                          textDecoration: "underline dotted",
                        }}
                      >
                        Audiences Table Selection
                      </IgTypography>
                    </IgTooltip>
                  }
                />
              </Grid>
            ) : null}
            <Grid item>
              <IgButton
                onClick={onBuildAudienceClick}
                disabled={
                  audienceState.storedAudience?.audienceType === "advanced" &&
                  rolloutsInfo &&
                  rolloutsInfo.dashboardTargeting.length === 0 &&
                  rolloutsInfo.analyticTableTargeting.length === 0
                }
                sx={{ minHeight: "32px", marginRight: "5px" }}
                variant={"outlined"}
              >
                Build Audience
              </IgButton>
              <IgTooltip title={buildAudienceButtonState}>
                <IconButton size={"small"}>
                  <Info size={18} />
                </IconButton>
              </IgTooltip>
            </Grid>
          </Grid>
        )}
      </IgPaper>
    </Grid>
  );
};
